/**
 * Runtime configuration accessor.
 *
 * Reads `window.__APP_CONFIG__`, which the embedded SPA server emits per
 * request, falling back to Vite's build-time `import.meta.env.VITE_*` so
 * `vite dev` works without that server. Decoupling the bundle from the
 * environment means one built artifact deploys to dev, staging and production.
 *
 * @see server/index.ts
 */

/**
 * Keys the admin tool reads at runtime.
 *
 * `FEATURE_ASSISTANT` is a flag (`'true'` enables it), routed through runtime
 * config rather than `import.meta.env` so `vite dev` — which does not load the
 * monorepo's `.env.portal` — still sees it, and so it can be toggled per
 * environment without a rebuild.
 *
 * `POSTHOG_KEY` / `POSTHOG_HOST` turn product analytics on; absent, the app
 * sends nothing (see lib/analytics.ts).
 *
 * `EMBED_FRAME_ANCESTORS` is the app server's `FRAME_ANCESTORS` (the same list
 * its CSP lets frame `/embed/proposals`), space-separated: the only parents
 * the embedded proposal editor will post messages to.
 *
 * `SALESFORCE_ORIGIN` is the Lightning origin Salesforce links open in; unset
 * means production (lib/salesforce.ts).
 */
type RuntimeConfigKey =
  | 'CLERK_PUBLISHABLE_KEY'
  | 'FEATURE_ASSISTANT'
  | 'POSTHOG_KEY'
  | 'POSTHOG_HOST'
  | 'EMBED_FRAME_ANCESTORS'
  | 'SALESFORCE_ORIGIN';

declare global {
  interface Window {
    __APP_CONFIG__?: Partial<Record<RuntimeConfigKey, string>>;
  }
}

/**
 * Whether a value is an unsubstituted build placeholder like `__FOO__`.
 *
 * @param value - Candidate value.
 * @returns True if it is a placeholder rather than a real value.
 */
function isPlaceholder(value: string): boolean {
  return /^__[A-Z0-9_]+__$/.test(value);
}

/**
 * Reads one runtime config value.
 *
 * @param key - The config key.
 * @returns The value, or undefined when unset.
 */
export function getRuntimeConfig(key: RuntimeConfigKey): string | undefined {
  const fromServer = window.__APP_CONFIG__?.[key];
  if (fromServer !== undefined && fromServer !== '' && !isPlaceholder(fromServer)) {
    return fromServer;
  }

  const fromBuild = import.meta.env[`VITE_ADMIN_${key}`] as string | undefined;
  if (fromBuild !== undefined && fromBuild !== '' && !isPlaceholder(fromBuild)) {
    return fromBuild;
  }

  return undefined;
}
